import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const CARD_GAP = 20;
const PEEK = 28; // px of next card visible on the right

export const ACADEMY_VIDEOS = [
  '/videos/academy-intro.mp4',
  'https://www.youtube.com/watch?v=-ZZ5bgIJQRM',
  'https://www.youtube.com/watch?v=Y7J_m5E2zUk',
  'https://www.youtube.com/watch?v=Y7J_m5E2zUk&list=PLG89uRsw_kIpJbYanse6rBZd3eRll8kyH&index=3',
  'https://www.youtube.com/watch?v=Y7J_m5E2zUk&list=PLG89uRsw_kIpJbYanse6rBZd3eRll8kyH&index=4',
  'https://www.youtube.com/watch?v=Y7J_m5E2zUk&list=PLG89uRsw_kIpJbYanse6rBZd3eRll8kyH&index=5',
  'https://www.youtube.com/watch?v=Y7J_m5E2zUk&list=PLG89uRsw_kIpJbYanse6rBZd3eRll8kyH&index=6',
];

// App-hosted videos (in public/videos/) play with a native player;
// YouTube videos play through YouTube's embed player
const isLocalVideo = (url: string): boolean => url.startsWith('/');

const getYoutubeEmbedUrl = (url: string): string | null => {
  try {
    const videoId = new URL(url).searchParams.get('v');
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0&enablejsapi=1`
      : null;
  } catch (err) {
    console.error('Invalid academy video URL', url, err);
    return null;
  }
};

const getVideoThumbnail = (url: string): string => {
  if (isLocalVideo(url)) return url.replace(/\.mp4$/, '.jpg');
  try {
    const videoId = new URL(url).searchParams.get('v') ?? '';
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  } catch (err) {
    console.error('Invalid academy video URL', url, err);
    return '';
  }
};

const AcademyCarousel: React.FC = () => {
  const { t } = useTranslation();
  const outerRef = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(ACADEMY_VIDEOS.length - 1);
  const [dragOffset, setDragOffset] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);
  const [isYoutubePaused, setIsYoutubePaused] = useState(false);
  const playingVideoRef = useRef<HTMLVideoElement>(null);
  const playingFrameRef = useRef<HTMLIFrameElement>(null);

  const enterFullscreen = useCallback(async () => {
    const target = playingVideoRef.current ?? playingFrameRef.current;
    if (!target) return;
    try {
      if (target.requestFullscreen) {
        await target.requestFullscreen();
      } else {
        // iOS Safari has its own fullscreen API for <video>
        const iosVideo = target as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
        iosVideo.webkitEnterFullscreen?.();
      }
    } catch (err) {
      console.error('Failed to enter fullscreen', err);
    }
  }, []);

  // Drive the YouTube player through its postMessage API — its own controls
  // are unreliable below YouTube's 200px minimum player size
  const toggleYoutubePlayback = useCallback(() => {
    const frame = playingFrameRef.current;
    if (!frame?.contentWindow) return;
    try {
      frame.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: isYoutubePaused ? 'playVideo' : 'pauseVideo',
          args: [],
        }),
        '*',
      );
      setIsYoutubePaused(!isYoutubePaused);
    } catch (err) {
      console.error('Failed to toggle YouTube playback', err);
    }
  }, [isYoutubePaused]);

  // Synced ref so event handlers always have the latest drag offset without stale closures
  const dragOffsetRef = useRef(0);
  const dragActive = useRef(false);
  const dragStartX = useRef(0);
  const dragStartTime = useRef(0);

  // Measure card width from the container so it's responsive
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const update = () => setCardWidth(el.offsetWidth - PEEK - CARD_GAP);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const slideWidth = cardWidth + CARD_GAP;

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, ACADEMY_VIDEOS.length - 1));
      // Stop any playing video when moving to another slide
      if (clamped !== currentIndex) setPlayingVideoUrl(null);
      setIsSnapping(true);
      setCurrentIndex(clamped);
      setDragOffset(0);
      dragOffsetRef.current = 0;
    },
    [currentIndex],
  );

  const settle = useCallback(() => {
    if (!dragActive.current) return;
    dragActive.current = false;
    setIsDragging(false);
    const dx = dragOffsetRef.current;
    const dt = Date.now() - dragStartTime.current;
    const velocity = Math.abs(dx) / Math.max(dt, 1); // px/ms
    if (velocity > 0.25 || Math.abs(dx) > cardWidth * 0.28) {
      goTo(dx > 0 ? currentIndex - 1 : currentIndex + 1);
    } else {
      goTo(currentIndex);
    }
  }, [cardWidth, currentIndex, goTo]);

  const startDrag = useCallback((clientX: number) => {
    dragActive.current = true;
    dragStartX.current = clientX;
    dragStartTime.current = Date.now();
    setIsSnapping(false);
    setIsDragging(true);
  }, []);

  const moveDrag = useCallback((clientX: number) => {
    if (!dragActive.current) return;
    const dx = clientX - dragStartX.current;
    dragOffsetRef.current = dx;
    setDragOffset(dx);
  }, []);

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => startDrag(e.touches[0].clientX);
  const onTouchMove = (e: React.TouchEvent) => moveDrag(e.touches[0].clientX);
  const onTouchEnd = () => settle();

  // Mouse handlers (desktop drag)
  const onMouseDown = (e: React.MouseEvent) => startDrag(e.pageX);
  const onMouseMove = (e: React.MouseEvent) => moveDrag(e.pageX);
  const onMouseUp = () => settle();

  const translateX = -currentIndex * slideWidth + PEEK + dragOffset;

  return (
    <section
      style={{
        marginTop: 24,
        background: '#DDE9F9',
        borderTop: '3px solid #1A1A2E',
        borderBottom: '3px solid #1A1A2E',
        padding: '20px 18px 20px',
        marginInline: -18,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          direction: 'rtl',
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 34,
              fontWeight: 900,
              color: TEXT_DARK,
              lineHeight: 1.1,
              letterSpacing: -0.5,
            }}
          >
            {t('academy_title', 'האקדמיה')}
          </div>
          <div style={{ fontSize: 14, color: TEXT_SECONDARY, marginTop: 4 }}>
            {t('academy_subtitle', 'טיפים ומדריכים לקריאה נכונה בוידאו')}
          </div>
        </div>
        <div style={{ fontSize: 40, lineHeight: 1 }}>🎓</div>
      </div>

      {/* Carousel viewport */}
      <div
        ref={outerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'pan-y',
        }}
      >
        {/* Sliding strip */}
        <div
          style={{
            display: 'flex',
            gap: CARD_GAP,
            direction: 'ltr',
            transform: `translateX(${translateX}px)`,
            transition: isSnapping
              ? 'transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              : 'none',
            willChange: 'transform',
            userSelect: 'none',
          }}
          onTransitionEnd={() => setIsSnapping(false)}
        >
          {[...ACADEMY_VIDEOS].reverse().map((url) => {
            const thumbnail = getVideoThumbnail(url);
            // Video that was clicked: play it right inside the card
            if (playingVideoUrl === url) {
              const youtubeEmbedUrl = getYoutubeEmbedUrl(url);
              const overlayButtonStyle: React.CSSProperties = {
                position: 'absolute',
                top: 8,
                width: 34,
                height: 34,
                borderRadius: 8,
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.5)',
                color: 'white',
                fontSize: 16,
                lineHeight: 1,
                cursor: 'pointer',
              };
              return (
                <div
                  key={url}
                  style={{
                    flexShrink: 0,
                    width: cardWidth || 260,
                    borderRadius: 14,
                    overflow: 'hidden',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                    background: 'black',
                  }}
                >
                  <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                    {youtubeEmbedUrl ? (
                      <>
                        <iframe
                          ref={playingFrameRef}
                          src={youtubeEmbedUrl}
                          title={t('academy_video_title', 'סרטון האקדמיה') as string}
                          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                          allowFullScreen
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                        />
                        <button
                          onClick={toggleYoutubePlayback}
                          aria-label={
                            t(
                              isYoutubePaused ? 'academy_video_play' : 'academy_video_pause',
                              isYoutubePaused ? 'הפעלה' : 'השהיה',
                            ) as string
                          }
                          style={{ ...overlayButtonStyle, insetInlineStart: 8 }}
                        >
                          {isYoutubePaused ? '▶' : '⏸'}
                        </button>
                      </>
                    ) : (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video
                        ref={playingVideoRef}
                        src={url}
                        title={t('academy_video_title', 'סרטון האקדמיה') as string}
                        poster={thumbnail}
                        controls
                        autoPlay
                        playsInline
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          display: 'block',
                        }}
                      />
                    )}
                    <button
                      onClick={enterFullscreen}
                      aria-label={t('academy_video_fullscreen', 'מסך מלא') as string}
                      style={{ ...overlayButtonStyle, insetInlineEnd: 8 }}
                    >
                      ⛶
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (Math.abs(dragOffsetRef.current) > 5) {
                    e.preventDefault();
                    return;
                  }
                  e.preventDefault();
                  setIsYoutubePaused(false);
                  setPlayingVideoUrl(url);
                }}
                draggable={false}
                style={{
                  flexShrink: 0,
                  width: cardWidth || 260,
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                  textDecoration: 'none',
                  display: 'block',
                }}
              >
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
                  <img
                    src={thumbnail}
                    alt=""
                    draggable={false}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                    }}
                  />
                  {/* Play button */}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'rgba(0,0,0,0.6)',
                        border: '2px solid rgba(255,255,255,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: 16,
                        paddingLeft: 3,
                      }}
                    >
                      ▶
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Dot indicators */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 6,
          marginTop: 12,
        }}
      >
        {ACADEMY_VIDEOS.map((_, i) => {
          const semanticIndex = ACADEMY_VIDEOS.length - 1 - currentIndex;
          return (
            <button
              key={i}
              onClick={() => goTo(ACADEMY_VIDEOS.length - 1 - i)}
              style={{
                width: i === semanticIndex ? 20 : 8,
                height: 8,
                borderRadius: 4,
                background: i === semanticIndex ? TEXT_DARK : 'rgba(26,26,46,0.25)',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'width 0.25s ease, background 0.25s ease',
              }}
              aria-label={`Video ${i + 1}`}
            />
          );
        })}
      </div>
    </section>
  );
};

export default AcademyCarousel;
